import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * `UiProvider` — the toast, dialog and panel hosts (spec M0-F7 §6.4, F4 §6/§7.7).
 *
 * No screen may invent its own overlay. Everything overlaying goes through one of the three
 * singleton hosts mounted here, which is what makes the layer order of §7.7 a property of the
 * app rather than of whoever wrote the last dialog.
 *
 * Mounting a second host throws in development (C7): two toast stacks is always a bug, and a
 * silent second stack is the kind of bug that only shows up in production.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import { Button } from '../components/wrappers.js';
import { useOverlayDismiss } from '../hooks/useOverlayDismiss.js';
import { icons } from '../icons/index.js';
import { IconButton } from '../components/wrappers.js';
/** Observed nowhere (monday's toast was not captured); these are F4 §7.7's stated defaults. */
const MAX_VISIBLE_TOASTS = 3;
const TOAST_MS = 5_000;
const TOAST_WITH_ACTION_MS = 10_000;
const UiContext = createContext(null);
export function useUi() {
    const value = useContext(UiContext);
    if (!value)
        throw new Error('useUi() requires <UiProvider>. Mount it once, in main.tsx.');
    return value;
}
/** The only toast entry point in the product (F4 §6). */
export function useToast() {
    return useUi().toast;
}
/**
 * The same toast for a component that is also rendered **outside** the provider — the module
 * harnesses (`/__ui/*`) and the jsdom unit tests mount screens on their own. Outside it the
 * returned function is a no-op rather than a throw, so a screen can report an error to a user
 * who has a provider without breaking the harness that has none.
 */
export function useOptionalToast() {
    const value = useContext(UiContext);
    return value?.toast ?? (() => 0);
}
/** Promise-returning `ConfirmDialog`, used by every destructive action in B1. */
export function useConfirm() {
    return useUi().confirm;
}
export function usePanelHost() {
    const { openPanel, closePanel, openPanelId } = useUi();
    return { openPanel, closePanel, openPanelId };
}
/* -------------------------------------------------------- singleton guard */
const mounted = new Set();
function useSingleton(name) {
    const [duplicate, setDuplicate] = useState(false);
    useEffect(() => {
        if (mounted.has(name)) {
            setDuplicate(true);
            return;
        }
        mounted.add(name);
        return () => {
            mounted.delete(name);
        };
    }, [name]);
    // Detected in the effect, raised in the render that follows, so it reaches the nearest error
    // boundary instead of an unhandled effect rejection. StrictMode's double-mount is safe: the
    // cleanup removes the name before the second mount adds it again.
    if (duplicate) {
        const message = `${name} is a singleton and is already mounted. Mount it once, inside <UiProvider>.`;
        if (process.env.NODE_ENV !== 'production')
            throw new Error(message);
        console.error(message);
    }
}
/** Test-only: React 19 StrictMode double-mounts in dev, so tests need a clean slate. */
export function __resetSingletons() {
    mounted.clear();
}
/* -------------------------------------------------------------- ToastHost */
/**
 * One row owns one timer. Doing it per row rather than per stack is what makes the 5 s / 10 s
 * deadlines absolute: a re-render because a sibling toast disappeared must not restart the
 * clock, and hovering must resume from the time that was left, not from the full duration.
 */
function ToastRow({ item, paused, onDismiss, }) {
    const remaining = useRef(item.durationMs);
    useEffect(() => {
        if (paused || remaining.current <= 0)
            return;
        const startedAt = Date.now();
        const timer = setTimeout(() => onDismiss(item.id), remaining.current);
        return () => {
            clearTimeout(timer);
            remaining.current -= Date.now() - startedAt;
        };
    }, [paused, item.id, onDismiss]);
    return (_jsxs("div", { className: "ez-toast", "data-kind": item.kind, role: item.kind === 'negative' ? 'alert' : 'status', "data-testid": "toast", children: [_jsx("span", { className: "ez-toast__message", children: item.message }), item.action ? (_jsx("button", { type: "button", className: "ez-toast__action", "data-testid": "toast-action", onClick: item.action.onClick, children: item.action.label })) : null, _jsx(IconButton, { icon: icons.close, ariaLabel: "Dismiss", size: "xs", kind: "tertiary", onClick: () => onDismiss(item.id) })] }));
}
function ToastHost({ toasts, onDismiss }) {
    useSingleton('ToastHost');
    const [paused, setPaused] = useState(false);
    if (toasts.length === 0)
        return null;
    return (_jsx("div", { className: "ez-toast-host", role: "region", "aria-label": "Notifications", "data-testid": "toast-host", onMouseEnter: () => setPaused(true), onMouseLeave: () => setPaused(false), onFocusCapture: () => setPaused(true), onBlurCapture: () => setPaused(false), children: toasts.map((item) => (_jsx(ToastRow, { item: item, paused: paused, onDismiss: onDismiss }, item.id))) }));
}
/* ------------------------------------------------------------- DialogHost */
function ConfirmDialogView({ record, onAnswer }) {
    const ref = useRef(null);
    /**
     * **P-260.** This used to be its own copy of the focus contract, and it was the broken one:
     * the opener was read in a *passive* effect — by which time a confirm raised from a menu row
     * already saw `<body>` — and restored **synchronously** on unmount, which the browser's own
     * "the focused element disappeared" handling then undid. The one hook does both properly and
     * takes Escape with it; this effect now only moves focus *into* the dialog, which the hook
     * deliberately never does.
     */
    useOverlayDismiss({ onClose: () => onAnswer(false) });
    useEffect(() => {
        ref.current?.querySelector('button')?.focus();
    }, []);
    return (_jsx("div", { className: "ez-modal-backdrop", "data-testid": "dialog-backdrop", onClick: () => onAnswer(false), children: _jsxs("div", { ref: ref, role: "dialog", "aria-modal": "true", "aria-labelledby": "ez-confirm-title", className: "ez-confirm", "data-testid": "confirm-dialog", onClick: (event) => event.stopPropagation(), children: [_jsx("h1", { id: "ez-confirm-title", className: "ez-confirm__title", children: record.title }), record.body ? _jsx("div", { className: "ez-confirm__body", children: record.body }) : null, _jsxs("div", { className: "ez-confirm__footer", children: [_jsx(Button, { kind: "tertiary", onClick: () => onAnswer(false), "data-testid": "confirm-cancel", children: record.cancelLabel ?? 'Cancel' }), _jsx(Button, { kind: "primary", color: record.danger ? 'negative' : undefined, onClick: () => onAnswer(true), "data-testid": "confirm-ok", children: record.confirmLabel ?? 'Confirm' })] })] }) }));
}
function DialogHost({ confirmations, onAnswer, }) {
    useSingleton('DialogHost');
    const top = confirmations[confirmations.length - 1];
    if (!top)
        return null;
    return _jsx(ConfirmDialogView, { record: top, onAnswer: (ok) => onAnswer(top.id, ok) });
}
/* -------------------------------------------------------------- PanelHost */
function PanelHost({ panel }) {
    useSingleton('PanelHost');
    if (!panel)
        return null;
    return (_jsx("div", { className: "ez-panel-host", "data-testid": "panel-host", children: panel.render() }));
}
export function UiProvider({ children }) {
    const [toasts, setToasts] = useState([]);
    const [confirmations, setConfirmations] = useState([]);
    const [panel, setPanel] = useState(null);
    const nextId = useRef(1);
    const dismissToast = useCallback((id) => {
        setToasts((current) => current.filter((item) => item.id !== id));
    }, []);
    const toast = useCallback((message, options = {}) => {
        const id = nextId.current++;
        const record = {
            id,
            message,
            kind: options.kind ?? 'normal',
            action: options.action,
            durationMs: options.durationMs ?? (options.action ? TOAST_WITH_ACTION_MS : TOAST_MS),
        };
        setToasts((current) => [...current, record].slice(-MAX_VISIBLE_TOASTS));
        return id;
    }, []);
    const confirm = useCallback((options) => {
        return new Promise((resolve) => {
            const id = nextId.current++;
            setConfirmations((current) => [...current, { ...options, id, resolve }]);
        });
    }, []);
    const answer = useCallback((id, ok) => {
        setConfirmations((current) => {
            current.find((item) => item.id === id)?.resolve(ok);
            return current.filter((item) => item.id !== id);
        });
    }, []);
    const openPanel = useCallback((next) => setPanel(next), []);
    const closePanel = useCallback(() => setPanel(null), []);
    const value = useMemo(() => ({ toast, dismissToast, confirm, openPanel, closePanel, openPanelId: panel?.id ?? null }), [toast, dismissToast, confirm, openPanel, closePanel, panel]);
    return (_jsxs(UiContext.Provider, { value: value, children: [children, _jsx(PanelHost, { panel: panel }), _jsx(DialogHost, { confirmations: confirmations, onAnswer: answer }), _jsx(ToastHost, { toasts: toasts, onDismiss: dismissToast })] }));
}
export function ConfirmDialog({ open, onConfirm, onCancel, ...options }) {
    if (!open)
        return null;
    return (_jsx(ConfirmDialogView, { record: { ...options, id: -1, resolve: () => undefined }, onAnswer: (ok) => (ok ? onConfirm() : onCancel()) }));
}
//# sourceMappingURL=UiProvider.js.map